// Note: Avatar utility functions

/** Generic person silhouette (head + shoulders on a neutral background), as a local data URL */
const GENERIC_PERSON_AVATAR = `data:image/svg+xml;base64,${btoa(
  '<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96">' +
  '<rect width="96" height="96" fill="#8e9aaf"/>' +
  '<circle cx="48" cy="37" r="17" fill="#ffffff"/>' +
  '<path d="M14 96c0-22 15-33 34-33s34 11 34 33z" fill="#ffffff"/>' +
  '</svg>'
)}`;

/**
 * Generates a default avatar URL with consistent colors based on the user's name
 * @param name The name to generate an avatar for
 * @returns URL for an avatar image (now uses local SVG for instant display)
 */
export const getDefaultAvatar = (name: string): string => {
  if (!name) return '';
  // A name with no letters in it (e.g. just a phone number, "(206) 940-3822" or "+12069403822")
  // has no initials — turning it into one gave nonsense like "(9". Use a generic person instead.
  if (!/\p{L}/u.test(name)) return GENERIC_PERSON_AVATAR;
  const nameParts = name.split(' ');
  const initials = nameParts.length > 1 
    ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`
    : nameParts[0][0];
  
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', 
    '#FFEEAD', '#D4A5A5', '#9B59B6', '#3498DB'
  ];
  
  const colorIndex = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
  const backgroundColor = colors[colorIndex];
  
  // Generate local SVG as base64 data URL for instant display (no external requests)
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96"><rect width="96" height="96" fill="${backgroundColor}"/><text x="48" y="56" font-family="Arial, sans-serif" font-size="32" font-weight="bold" text-anchor="middle" fill="white">${initials.toUpperCase()}</text></svg>`;
  
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/**
 * Proxies external images to avoid tracking prevention blocks
 * 
 * @param url The original image URL
 * @returns A proxied URL or the original if no proxy is needed
 */
export const proxyImage = (url: string | undefined | null): string | undefined => {
  if (!url || typeof url !== 'string') return undefined;

  // Convert Wix image URLs to static URLs
  if (url.startsWith('wix:image://')) {
    const match = url.match(/^wix:image:\/\/v1\/([^\/#]+)/);
    if (match) {
      const imageId = match[1];
      return `https://static.wixstatic.com/media/${imageId}/v1/fill/w_80,h_80,al_c,q_70/file.jpg`;
    }

    // Fallback pattern
    const fallbackId = url.replace(/^wix:image:\/\//, '').split(/[\/#]/)[1] || '';
    if (fallbackId) {
      return `https://static.wixstatic.com/media/${fallbackId}`;
    }
  }

  // Proxy problematic URLs through Firebase imageProxy HTTP function
  if (url.includes('fbsbx.com') || url.includes('googleusercontent.com') || url.includes('cdn.jsdelivr.net')) {
    // Decode first to handle already-encoded URLs, then encode to avoid double-encoding
    const decodedUrl = decodeURIComponent(url);
    const encodedUrl = encodeURIComponent(decodedUrl);
    return `https://us-central1-rsmaidsbooking.cloudfunctions.net/imageProxy?proxy=${encodedUrl}`;
  }

  return url;
};

// Firebase callable helper for proxying images. This contacts the `wixAdmin` callable
// function and asks it to proxy the image. Returns a URL (proxied/public) or the
// original URL on failure.
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Asynchronously requests a proxied image URL from the Firebase callable function `wixAdmin`.
 * @param url The original image URL
 * @returns proxied URL string or original URL on error
 */
export async function proxyImageAsync(url: string): Promise<string> {
  if (!url) return url;

  // Only proxy known problematic providers
  if (!(url.includes('fbsbx.com') || url.includes('googleusercontent.com'))) {
    return url;
  }

  try {
    const functions = getFunctions();
    const wixAdminCallable = httpsCallable(functions, 'wixAdmin');

    // Call the callable function with the same semantics the HTTP endpoint used:
    // pass proxy=image and url; cloud function should return an object containing a proxied URL.
    const result = await wixAdminCallable({ proxy: 'image', url });

    // The callable may return different shapes; attempt to find a usable URL.
    const data = result?.data as unknown;
    if (!data) return url;

    // Narrow to a plain object for safe property access
    if (typeof data === 'object' && data !== null) {
      const obj = data as Record<string, unknown>;
      const proxied = obj['proxiedUrl'];
      if (typeof proxied === 'string' && proxied) return proxied;

      const download = obj['downloadUrl'];
      if (typeof download === 'string' && download) return download;

      const maybeUrl = obj['url'];
      if (typeof maybeUrl === 'string' && maybeUrl) return maybeUrl;
    }

    // Fallback: if the function returned a raw string
    if (typeof data === 'string' && data) return data;

    return url;
  } catch (err) {
  console.error('proxyImageAsync: failed to proxy image via callable function', err);
    return url;
  }
}
